import moment from "moment";
import { useCallback, useState, useEffect } from "react";
import { IoRemove, IoTrendingDown, IoTrendingUp } from "react-icons/io5";
import { Link } from "react-router-dom";
import { Button, cn } from "rizzui";
import { toast } from "sonner";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import CustomTable from "../../../components/table/CustomTable";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { menuRoutes } from "../../../router/menu";
import { UserService } from "../../../services/user/userService";
import { RankingsApiResponse } from "../../../type/entities/ranking-type";
import UsersRankingsFilters from "./UsersRankingsFilters";
import { HiOutlineChevronDown } from "react-icons/hi";

const columnOrder = [
    'id',
    'name',
    'lastName',
    'commercialNetworkName',
    'commercialNetworkCode',
    'departmentName',
    'dailyCurrent',
    'dailyPrevious',
    'weeklyCurrent',
    'weeklyPrevious',
    'monthlyCurrent',
    'monthlyPrevious',
];


/*--------------------------------------------------------------------------------------------------------
 * EN: Commissions information const and methods

 * ES: Constantes y métodos de comisiones
---------------------------------------------------------------------------------------------------------*/
const getDailyReward2024 = (points: number) => {
    if (!points) return 0;

    const basePoints = Math.floor(points);
    const isHalf = points % 1 === 0.5;

    let base = 0;

    if (basePoints === 1) base = 0;
    else if (basePoints === 2) base = 20;
    else if (basePoints === 3) base = 30;
    else if (basePoints === 4) base = 40;
    else if (basePoints >= 5) base = 50;

    if (isHalf) base += 10;

    return base;
};


const getWeeklyReward2024 = (points: number) => {
    if (!points) return 0;

    const p = Math.floor(points);

    if (p <= 4) return 0;
    if (p === 5) return 50;
    if (p === 6) return 75;
    if (p === 7) return 90;
    if (p === 8) return 100;
    if (p === 9) return 120;
    if (p === 10) return 150;
    if (p >= 11) return 180;

    return 0;
};

const MONTHLY_2024 = [
    { min30: 12, min25: 11, value: 75 },
    { min30: 13, min25: 12, value: 150 },
    { min30: 14, min25: 13, value: 225 },
    { min30: 15, min25: 14, value: 300 },
    { min30: 16, min25: 15, value: 375 },
    { min30: 17, min25: 16, value: 450 },
    { min30: 18, min25: 17, value: 525 },
    { min30: 19, min25: 18, value: 600 },
    { min30: 20, min25: 19, value: 675 },
    { min30: 21, min25: 20, value: 750 },
    { min30: 22, min25: 21, value: 900 },
    { min30: 23, min25: 22, value: 1050 },
    { min30: 24, min25: 23, value: 1200 },
    { min30: 25, min25: 24, value: 1350 },
    { min30: 26, min25: 25, value: 1500 },
];

const getMonthlyReward2024 = (points: number, weeklyHours: number) => {
    if (!points) return 0;

    const p = Math.floor(points);

    // 30h o más → tramo 30h
    if (weeklyHours >= 30) {
        const row = MONTHLY_2024.find(r => r.min30 === p);
        return row?.value ?? 0;
    }

    // Entre 25 y 29.999... → tramo 25h
    if (weeklyHours >= 25) {
        const row = MONTHLY_2024.find(r => r.min25 === p);
        return row?.value ?? 0;
    }

    // Menos de 25h → sin incentivo
    return 0;
};

const COMMISSIONS_2026 = [
  { points: 10, impVenta: 30, total: 30, pago1: 30, pago2: 0 },
  { points: 11, impVenta: 30, total: 30, pago1: 30, pago2: 0 },
  { points: 12, impVenta: 75, total: 75, pago1: 75, pago2: 0 },
  { points: 13, impVenta: 25, total: 100, pago1: 75, pago2: 25 },
  { points: 14, impVenta: 30, total: 130, pago1: 75, pago2: 55 },
  { points: 15, impVenta: 35, total: 165, pago1: 90, pago2: 75 },
  { points: 16, impVenta: 40, total: 205, pago1: 110, pago2: 95 },
  { points: 17, impVenta: 45, total: 250, pago1: 125, pago2: 125 },
  { points: 18, impVenta: 50, total: 300, pago1: 150, pago2: 150 },
  { points: 19, impVenta: 50, total: 350, pago1: 175, pago2: 175 },
  { points: 20, impVenta: 50, total: 400, pago1: 200, pago2: 200 },
  { points: 21, impVenta: 50, total: 450, pago1: 225, pago2: 225 },
  { points: 22, impVenta: 50, total: 500, pago1: 250, pago2: 250 },
  { points: 23, impVenta: 50, total: 550, pago1: 275, pago2: 275 },
  { points: 24, impVenta: 50, total: 600, pago1: 300, pago2: 300 },
  { points: 25, impVenta: 100, total: 700, pago1: 350, pago2: 350 },
  { points: 26, impVenta: 50, total: 750, pago1: 375, pago2: 375 },
  { points: 27, impVenta: 50, total: 800, pago1: 400, pago2: 400 },
  { points: 28, impVenta: 50, total: 850, pago1: 425, pago2: 425 },
  { points: 29, impVenta: 50, total: 900, pago1: 450, pago2: 450 },
  { points: 30, impVenta: 100, total: 1000, pago1: 500, pago2: 500 }
];

const getMonthlyReward2026 = (points: number) => {
    if (!points) return 0;

    const p = Math.floor(points);
    const row = COMMISSIONS_2026.find(r => r.points === p);

    // Mostramos el total del convenio 2026
    return row?.total ?? 0;
};


/*--------------------------------------------------------------------------------------------------------
 * EN: Score difference and trend calculation
 * ES: Cálculo de diferencia y tendencia de puntuación
 ---------------------------------------------------------------------------------------------------------*/
// Function to calculate score difference and trend
const calculateScoreDifference = (current: number, previous: number) => {
    if (previous === 0 || previous === null || previous === undefined) {
        return {
            absolute: current,
            percentage: current > 0 ? 100 : 0,
            trend: current > 0 ? 'up' : current < 0 ? 'down' : 'neutral'
        };
    }

    const absolute = current - previous;
    const percentage = ((absolute / Math.abs(previous)) * 100);
    const trend = absolute > 0 ? 'up' : absolute < 0 ? 'down' : 'neutral';

    return { absolute, percentage, trend };
};

// Component to show score evolution
const ScoreEvolutionIndicator = ({ current, previous }: {
    current: number;
    previous: number;
}) => {
    const { absolute, percentage, trend } = calculateScoreDifference(current, previous);
    const [showTooltip, setShowTooltip] = useState(false);

    const getTrendIcon = () => {
        switch (trend) {
            case 'up':
                return <IoTrendingUp className="h-3 w-3" />;
            case 'down':
                return <IoTrendingDown className="h-3 w-3" />;
            default:
                return <IoRemove className="h-3 w-3" />;
        }
    };

    const getTrendColor = () => {
        switch (trend) {
            case 'up':
                return 'text-green-700 bg-green-100';
            case 'down':
                return 'text-red-700 bg-red-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <div className="relative flex items-center gap-2">

            {/* Puntos */}
            <span className="text-sm font-semibold text-gray-900">
                Puntos: {current?.toFixed(2)}
            </span>

            {/* Badge de evolución */}
            <div
                onClick={() => setShowTooltip(!showTooltip)}
                className={cn(
                    "cursor-pointer flex items-center gap-1 px-2 py-[2px] rounded-full text-xs font-medium select-none",
                    getTrendColor()
                )}
            >
                {getTrendIcon()}
                <span>{percentage.toFixed(2)}%</span>
            </div>

            {/* Tooltip con la puntuación anterior */}
            {showTooltip && (
                <div className="
                    absolute top-6 left-20
                    bg-white border border-gray-300 rounded-md shadow-md
                    px-3 py-2 text-xs text-gray-700 z-10
                ">
                    Puntuación anterior: <strong>{previous?.toFixed(2)}</strong>
                </div>
            )}
        </div>
    );
};

/*--------------------------------------------------------------------------------------------------------
 * EN: UsersRankingsList component
 * ES: Componente UsersRankingsList
 ---------------------------------------------------------------------------------------------------------*/
const UsersRankingsList = () => {

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { userCan } = usePrivilege();
    const service = new UserService();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [loadingExport, setLoadingExport] = useState<boolean>(false);
    const [activeModel, setActiveModel] = useState<string>('2026');
    const [openPayments, setOpenPayments] = useState<{[key: string]: boolean}>({});
    const canSeeCommissions = userCan("show_user_commissions", "user");

    const togglePayments = (id: string) => {
        setOpenPayments(prev => ({ ...prev, [id]: !prev[id] }));
    };

    //------------------ cargar convenio activo ------------------
    useEffect(() => {
        (async () => {
            try {
                const response = await service.getCompanyConfigurationInfo();
                const config = response.getResponseData()?.data;
                if (config?.activeContractCommissionModel) {
                    setActiveModel(config.activeContractCommissionModel);
                }
            } catch (e) {
                console.error("Error obteniendo modelo de comisiones", e);
            }
        })();
    }, []);

    const [data, loading] = useFetch(useCallback(async () => {
        const response = await service.listUsersWithPoints(filters);
        return response.getResponseData() as RankingsApiResponse;
    }, [filters]));

    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR PUNTUACIONES DE USUARIOS
     * @EN EXPORT USER SCORES
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleExport = async () => {
        setLoadingExport(true);
        try {
            const response = (await service.exportUsersWithPoints(filters));
            if (response) {
                const fileData = response.getResponseData();
                const blob = new Blob([fileData]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `puntuaciones_${moment().format('DD-MM-YYYY')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                toast.error('Error al exportar las puntuaciones');
            }
        } catch (error: any) {
            toast.error(error.message || 'Error al exportar las puntuaciones');
        } finally {
            setLoadingExport(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'order-types-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={['id', 'lastName', 'commercialNetworkCode', 'dailyPrevious', 'weeklyPrevious', 'monthlyPrevious']}
                    overrideColumns={[
                        {
                            key: 'name',
                            render: (row: any) => {
                                return (
                                    userCan('get_user', 'user')
                                        ? (
                                            <Link to={`${menuRoutes.users.path}/${row.row.original.id}/profile${menuRoutes.users.profile.info}`}>
                                                <span className="font-bold text-primary">{row.row.original.name}</span>
                                            </Link>
                                        )
                                        : <span>{row.row.original.name}</span>
                                )
                            },
                        },
                        {
                            key: "dailyCurrent",
                            render: (row: any) => {
                                const current = Number(row.row.original.dailyCurrent);
                                const previous = Number(row.row.original.dailyPrevious);

                                // Si NO tiene permiso → ocultar incentivos
                                if (!canSeeCommissions) {
                                    return (
                                        <div className="flex flex-col">
                                            <ScoreEvolutionIndicator current={current} previous={previous} />
                                        </div>
                                    );
                                }

                                const reward =
                                    activeModel === "2024/25"
                                        ? getDailyReward2024(current)
                                        : null;

                                if (!reward) {
                                    return (
                                        <div className="flex flex-col">
                                            <ScoreEvolutionIndicator current={current} previous={previous} />
                                        </div>
                                    );
                                }
                                
                                return (
                                    <div className="flex flex-col">
                                        <ScoreEvolutionIndicator current={current} previous={previous} />
                                        <span className="text-xs font-semibold text-blue-700 mt-1">
                                            Incentivo: {reward} €
                                        </span>
                                    </div>
                                );
                            }
                        },
                        {
                            key: "weeklyCurrent",
                            render: (row: any) => {
                                const current = Number(row.row.original.weeklyCurrent);
                                const previous = Number(row.row.original.weeklyPrevious);

                                // Si NO tiene permiso → ocultar incentivos
                                if (!canSeeCommissions) {
                                    return (
                                        <div className="flex flex-col">
                                            <ScoreEvolutionIndicator current={current} previous={previous} />
                                        </div>
                                    );
                                }

                                const reward =
                                    activeModel === "2024/25"
                                        ? getWeeklyReward2024(current)
                                        : null;

                                if (!reward) {
                                    return (
                                        <div className="flex flex-col">
                                            <ScoreEvolutionIndicator current={current} previous={previous} />
                                        </div>
                                    );
                                }

                                return (
                                    <div className="flex flex-col">
                                        <ScoreEvolutionIndicator current={current} previous={previous} />
                                        <span className="text-xs font-semibold text-blue-700 mt-1">
                                            Incentivo: {reward} €
                                        </span>
                                    </div>
                                );
                            }
                        },
                        {
                            key: "monthlyCurrent",
                            render: (row: any) => {
                                const current = Number(row.row.original.monthlyCurrent);
                                const previous = Number(row.row.original.monthlyPrevious);
                                const weeklyHours = Number(row.row.original.weeklyHours ?? 30);

                                if (!canSeeCommissions) {
                                    return <ScoreEvolutionIndicator current={current} previous={previous} />;
                                }

                                let reward = 0;
                                let extra: any = null;

                                if (activeModel === "2024/25") {
                                    reward = getMonthlyReward2024(current, weeklyHours);
                                }

                                if (activeModel === "2026") {
                                    const p = Math.floor(current);
                                    const row26 = COMMISSIONS_2026.find(r => r.points === p);
                                    reward = row26?.total ?? 0;
                                    extra = row26 ?? null;
                                }

                                return (
                                    <div className="flex flex-col">

                                        {/* PUNTOS Y % EVOLUCIÓN */}
                                        <ScoreEvolutionIndicator current={current} previous={previous} />

                                        {/* INFORMACIÓN DE INCENTIVOS */}
                                        {activeModel === "2024/25" && reward && (
                                            <span className="text-xs font-semibold text-blue-700 mt-1">
                                                Incentivo: {reward} €
                                            </span>
                                        )}

                                        {activeModel === "2026" && extra && (
                                            <div className="text-[11px] text-gray-700 mt-1">
                                                <div className="font-semibold mb-1">Información de incentivos</div>

                                                <div className="flex justify-between">
                                                        <span>Primer pago</span>
                                                        <span className="font-semibold">{extra.pago1} €</span>
                                                    </div>

                                                    <div className="flex justify-between">
                                                        <span>Segundo pago</span>
                                                        <span className="font-semibold">{extra.pago2} €</span>
                                                    </div>

                                                    <div className="flex justify-between border-t pt-1 mt-1 font-semibold">
                                                        <span>Total</span>
                                                        <span>{reward} €</span>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                );
                            }
                        }
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as RankingsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    <Button
                        variant={'outline'}
                        color={'primary'}
                        size={'sm'}
                        onClick={handleExport}
                        className='p-3 text-nowrap'
                        disabled={loadingExport}
                        isLoading={loadingExport}
                    >
                        Exportar
                    </Button>
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Puntuaciones'}>
                <UsersRankingsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default UsersRankingsList;
